/**
 * Gate for in-app paths the embedded Atlas frame asks warbler to navigate to.
 *
 * `PARENT_NAVIGATE` used to reach only `$router.push(path)`, which treats its
 * argument as a route and will not execute a `javascript:` URL or leave the
 * origin. AS-10885 added `reload: true`, and that branch calls
 * `shopifyBridge.redirectApp` instead — `window.location.assign(path)`
 * standalone, `open(path, '_self')` embedded. Both of those WILL follow an
 * absolute URL off-origin and will run a `javascript:` URL, so the widened
 * primitive needs a gate the router gave us for free.
 *
 * `isAllowedExternalRedirect` (utils/externalRedirect.ts) is the sibling rule
 * for PARENT_REDIRECT_EXTERNAL and deliberately does NOT cover this: that one
 * allows exactly two Shopify hosts, whereas this one allows no host at all.
 * Same doctrine though — warbler is the side that navigates, so it must never
 * assume atlas checked first.
 */

/**
 * True when `path` is a same-app absolute path, safe for `redirectApp`.
 *
 * Accepts one leading `/` followed by anything that is not a second `/` or a
 * `\`. Rejects:
 *   - `//evil.com/x` and `/\evil.com/x` — protocol-relative, resolves off-origin
 *   - `https://evil.com` / `javascript:alert(1)` — any scheme, no leading `/`
 *   - a bare relative path (`reports/1`) — ambiguous against the current URL
 *   - control characters anywhere, including the `\t\n\r` browsers strip from
 *     schemes before parsing (`java\tscript:` is a live `javascript:` URL)
 *   - backslashes anywhere, which several browsers normalise to `/`
 */
export function isSameAppPath(path: unknown): path is string {
  if (typeof path !== 'string' || path === '') return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return false;
  return /^\/(?!\/)/.test(path);
}
